import {Box, Paper, Typography} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import {
  apiV2,
  checkIfEligibleForPasswordReset,
  checkIfUserExists,
} from "api/firebase";
import React, {ReactElement, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {useForm} from "react-hook-form";
import {Link, useNavigate} from "react-router-dom";

import {
  createDebouncedAsyncValidator,
  emailValidationPattern,
} from "tools/validations";
import Alert from "view/components/Alert";
import Copyright from "view/components/Copyright";
import Form, {ActionButton, BasicInput} from "view/components/Form";
import PublicLanguagePicker from "view/components/LanguagePicker/PublicLanguagePicker";
import KeyIcon from "view/images/KeyIcon";
import {LOGIN_PATH} from "view/routes";
import useStyles from "./style";

type FormData = {
  email: string;
};

type EmailError = "notFound" | "notEligible";

const EMAIL_ERROR_MESSAGES: Record<EmailError, string> = {
  notFound:
    "The email address you entered doesn't exist. Please check your entry.",
  notEligible: "Something went wrong.",
};

const ForgotPassword = (): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslate();
  const history = useNavigate();
  const [emailError, setEmailError] = useState<EmailError | null>(null);
  const form = useForm<FormData>({
    mode: "all",
  });

  const validateEmailExists = useMemo(
    () =>
      createDebouncedAsyncValidator<boolean>(async (email) => {
        if (!email || !emailValidationPattern.test(email)) {
          return true;
        }
        const isEligible = await checkIfEligibleForPasswordReset(email);
        if (isEligible) {
          setEmailError(null);
          return true;
        }
        const userExists = await checkIfUserExists(email);
        setEmailError(userExists ? "notEligible" : "notFound");
        return false;
      }),
    []
  );

  const onSubmit = async (data: FormData) => {
    try {
      const {email} = data;
      await apiV2.auth.sendResetPasswordLink({
        email: email.trim().toLowerCase(),
      });

      history(LOGIN_PATH);
    } catch (e) {
      throw new Error(e);
    }
  };

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.container}>
          <Paper elevation={0} className={classes.paper}>
            <Form
              form={form}
              className={classes.form}
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <CssBaseline />
              <KeyIcon />
              <Typography className={classes.signInText}>
                <T keyName="global.login.forgot_password_title" />
              </Typography>
              <BasicInput
                className={classes.field}
                name="email"
                rules={{
                  pattern: {
                    value: emailValidationPattern,
                    message: t("common.validation.email"),
                  },
                  validate: validateEmailExists,
                }}
                onBlur={() => {
                  setEmailError(null);
                }}
                label={t("common.field.email")}
              />
              {emailError ? (
                <Alert severity="error" className={classes.alert}>
                  {emailError === "notFound" ? (
                    <T keyName="global.login.forgot_password_email_not_found" />
                  ) : (
                    EMAIL_ERROR_MESSAGES[emailError]
                  )}
                </Alert>
              ) : (
                <Alert severity="info" className={classes.alert}>
                  <T keyName="global.login.forgot_password_info" />
                </Alert>
              )}
              <Box className={classes.actions}>
                <Link to={LOGIN_PATH} className={classes.signInLink}>
                  <T keyName="global.login.remember_password" />
                </Link>
                <ActionButton
                  type="submit"
                  text={t("global.login.send_link")}
                  color="primary"
                  className={classes.buttonText}
                  variant="contained"
                />
              </Box>
            </Form>
          </Paper>
        </Box>
        <Box>
          <PublicLanguagePicker />
        </Box>
        <Box>
          <Copyright />
        </Box>
      </Box>
    </>
  );
};

export default ForgotPassword;
